// Phase 0094-P2 — EPF Compliance API client.
import apiClient from '@/shared/api/client'

export interface EPFConfig {
  id: string
  config_code: string
  effective_from: string
  effective_to: string | null
  employee_pf_percent: number
  employer_pf_percent: number
  eps_percent: number
  epf_employer_balance_percent: number
  pf_wage_ceiling: number
  pension_wage_ceiling: number
  edli_percent: number
  edli_wage_ceiling: number
  epf_admin_charge_percent: number
  edli_admin_charge_percent: number
  rounding_rule: string
  approval_status: string
  active: boolean
  system_seeded: boolean
  admin_modified_at: string | null
}

export interface WageHeadMapping {
  id: string
  head_code: string
  head_name: string
  head_type: string
  included_in_epf_wage: boolean
  included_in_eps_wage: boolean
  included_in_edli_wage: boolean
  effective_from: string
  active: boolean
  system_seeded: boolean
  admin_modified_at: string | null
}

export interface EPFProfile {
  id: string
  employee_id: string
  pf_applicable: boolean
  uan: string | null
  pf_number: string | null
  pf_join_date: string | null
  pf_exit_date: string | null
  previous_pf_number: string | null
  previous_uan: string | null
  eps_applicable: boolean
  voluntary_pf_applicable: boolean
  voluntary_pf_percent: number | null
  voluntary_pf_amount: number | null
  pf_wage_ceiling_applicable: boolean
  pension_wage_ceiling_applicable: boolean
  international_worker_flag: boolean
  excluded_employee_flag: boolean
  nominee_details_available: boolean
  kyc_status: string
  aadhaar_seeded: boolean
  pan_seeded: boolean
  bank_seeded: boolean
  approval_status: string
  active: boolean
}

export interface EPFRegisterRow {
  employee_id: string
  emp_code: string | null
  emp_name: string | null
  uan: string | null
  pf_number: string | null
  epf_applicable: boolean
  epf_wages: number
  eps_wages: number
  edli_wages: number
  employee_epf: number
  employer_epf: number
  employer_eps: number
  edli: number
  epf_admin: number
  edli_admin: number
  total_employee: number
  total_employer: number
  total_liability: number
  voluntary_pf: number
  status: string
  error: string | null
}

export interface EPFCompanySummary {
  pf_applicable_employees: number
  total_epf_wages: number
  employee_epf: number
  employer_epf: number
  employer_eps: number
  edli: number
  epf_admin_charges: number
  edli_admin_charges: number
  voluntary_pf: number
  total_liability: number
}

export interface EPFChallan extends EPFCompanySummary {
  challan_total: number
}

export interface EPFDashboard {
  summary: EPFCompanySummary
  review_required: number
  profile_missing: number
  total_rows: number
}

export interface KYCRow {
  employee_id: string
  emp_code: string | null
  emp_name: string | null
  uan: string | null
  pf_number: string | null
  kyc_status: string
  aadhaar_seeded: boolean
  pan_seeded: boolean
  bank_seeded: boolean
  missing: string[]
}

const P = '/payroll/epf'

export const epfApi = {
  // Config
  listConfigs: async () => (await apiClient.get<EPFConfig[]>(`${P}/configs`)).data,
  updateConfig: async (id: string, body: Partial<EPFConfig>) =>
    (await apiClient.patch<EPFConfig>(`${P}/configs/${id}`, body)).data,

  // Wage head mappings
  listMappings: async () =>
    (await apiClient.get<WageHeadMapping[]>(`${P}/wage-head-mappings`)).data,
  updateMapping: async (id: string, body: Partial<WageHeadMapping>) =>
    (await apiClient.patch(`${P}/wage-head-mappings/${id}`, body)).data,

  // Profile
  upsertProfile: async (body: Partial<EPFProfile> & { employee_id: string }) =>
    (await apiClient.post<EPFProfile>(`${P}/profiles`, body)).data,
  getProfile: async (employee_id: string) =>
    (await apiClient.get<EPFProfile>(`${P}/profiles/${employee_id}`)).data,

  // Calc + reports
  calculate: async (run_id: string) =>
    (await apiClient.post<{ run_id: string; config_code: string; calculated: number; not_applicable: number; profile_missing: number; review_required: number }>(
      `${P}/runs/${run_id}/calculate`)).data,
  dashboard: async (run_id: string) =>
    (await apiClient.get<EPFDashboard>(`${P}/runs/${run_id}/dashboard`)).data,
  register: async (run_id: string) =>
    (await apiClient.get<EPFRegisterRow[]>(`${P}/runs/${run_id}/register`)).data,
  summary: async (run_id: string) =>
    (await apiClient.get<EPFCompanySummary>(`${P}/runs/${run_id}/summary`)).data,
  challan: async (run_id: string) =>
    (await apiClient.get<EPFChallan>(`${P}/runs/${run_id}/challan`)).data,
  wageRegister: async (run_id: string) =>
    (await apiClient.get<EPFRegisterRow[]>(`${P}/runs/${run_id}/wage-register`)).data,
  contributionRegister: async (run_id: string) =>
    (await apiClient.get<EPFRegisterRow[]>(`${P}/runs/${run_id}/contribution-register`)).data,
  voluntary: async (run_id: string) =>
    (await apiClient.get(`${P}/runs/${run_id}/voluntary-pf`)).data,
  kyc: async () => (await apiClient.get<KYCRow[]>(`${P}/kyc`)).data,
  applicableExcluded: async () =>
    (await apiClient.get(`${P}/applicable-excluded`)).data,
  joiners: async (year: number, month: number) =>
    (await apiClient.get(`${P}/joiners`, { params: { year, month } })).data,
  exits: async (year: number, month: number) =>
    (await apiClient.get(`${P}/exits`, { params: { year, month } })).data,

  // Forms
  form5: async (year: number, month: number) =>
    (await apiClient.get(`${P}/forms/5`, { params: { year, month } })).data,
  form10: async (year: number, month: number) =>
    (await apiClient.get(`${P}/forms/10`, { params: { year, month } })).data,
  form12a: async (run_id: string) =>
    (await apiClient.get(`${P}/forms/12a`, { params: { run_id } })).data,
  form3a: async (year: number, employee_id?: string) =>
    (await apiClient.get(`${P}/forms/3a`, { params: { year, employee_id } })).data,
  form6a: async (year: number) =>
    (await apiClient.get(`${P}/forms/6a`, { params: { year } })).data,
  ecrExport: async (run_id: string) =>
    (await apiClient.get(`${P}/runs/${run_id}/ecr-export`)).data,
}
